package at.theatervogelweide.admin.web.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.connections.ConnectionsService
import at.theatervogelweide.admin.contract.connections.McpConnection
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminDataTable
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.DataTableColumn
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.admin.web.screens.acts.formatDate
import at.theatervogelweide.admin.web.screens.acts.formatDateTime
import kotlinx.coroutines.launch
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private val card = Bem("admin-card")
private val listPanel = Bem("admin-list-panel")
private val alert = Bem("admin-alert")
private val connections = Bem("admin-connections")

/**
 * Verbundene Anwendungen (#34): the MCP clients admins allowed, such as Claude, and "Zugriff entziehen". Connecting
 * happens in the client itself, which opens the admin app's sign-in and consent page.
 */
@Composable
fun ConnectionsScreen(rpc: RpcClient) {
    val service = remember(rpc) { rpc.withService<ConnectionsService>() }
    val scope = rememberCoroutineScope()
    var entries by remember { mutableStateOf<List<McpConnection>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var toRevoke by remember { mutableStateOf<McpConnection?>(null) }
    var loadAttempt by remember { mutableStateOf(0) }

    LaunchedEffect(service, loadAttempt) {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> {
                entries = result.value
                error = null
            }

            is AdminResult.Failure -> error = result.error.message
        }
    }

    fun revoke(connection: McpConnection) {
        toRevoke = null
        scope.launch {
            when (val result = rpcCall { service.revoke(connection.id) }) {
                // already gone counts as done
                is AdminResult.Success -> loadAttempt++
                is AdminResult.Failure -> if (result.error.code == ErrorCode.NOT_FOUND) {
                    loadAttempt++
                } else {
                    error = result.error.message
                }
            }
        }
    }

    AdminPageHeader("Verbundene Anwendungen")

    AdminPageBody {
        Section({
            classes(card.block, listPanel.block, connections.block)
            attr("aria-label", "Verbundene Anwendungen")
        }) {
            Div({ classes(connections.element("intro").className) }) {
                AdminCallout {
                    Text(
                        "KI-Assistenten wie Claude können über MCP mit der Verwaltung arbeiten, sobald du sie in der Anwendung " +
                            "verbindest und mit deinem Konto zustimmst. Kontaktdaten von Personen bekommen sie nie zu sehen, " +
                            "löschen können sie nichts. Was sie ändern, steht hier bei der Verbindung.",
                    )
                }
            }

            error?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            val all = entries
            when {
                all == null -> if (error == null) P({ classes(listPanel.element("message").className) }) { Text("Lade …") }

                all.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Keine Anwendung ist verbunden.")
                }

                else -> AdminDataTable(
                    rows = all,
                    key = { it.id },
                    caption = "Verbundene Anwendungen",
                    inCard = true,
                    columns = listOf(
                        DataTableColumn("Anwendung") { connection ->
                            Div {
                                Text(connection.clientName)
                                Span({ classes(connections.element("host").className) }) { Text(connection.clientHost) }
                            }
                        },
                        DataTableColumn("Darf") { connection -> Text(if (connection.canWrite) "Lesen und bearbeiten" else "Lesen") },
                        DataTableColumn("Erlaubt von") { connection -> Text(connection.adminName) },
                        DataTableColumn("Seit") { connection -> Text(formatDate(connection.connectedAt)) },
                        DataTableColumn("Zuletzt genutzt") { connection -> Text(formatDateTime(connection.lastUsedAt)) },
                        DataTableColumn("Endet") { connection -> Text(formatDate(connection.expiresAt)) },
                        DataTableColumn("Änderungen") { connection -> ChangesCell(connection) },
                        DataTableColumn("Aktion", headerHidden = true) { connection ->
                            AdminButton(variant = ButtonVariant.DANGER, onClick = { toRevoke = connection }) { Text("Zugriff entziehen") }
                        },
                    ),
                )
            }
        }
    }

    AdminConfirmDialog(
        open = toRevoke != null,
        id = "zugriff-entziehen",
        title = "Zugriff entziehen?",
        message = "„${toRevoke?.clientName.orEmpty()}“ kann sofort nicht mehr auf die Verwaltung zugreifen. Um sie wieder " +
            "zu nutzen, muss sie jemand neu verbinden.",
        confirmLabel = "Zugriff entziehen",
        cancelLabel = "Abbrechen",
        onConfirm = { toRevoke?.let(::revoke) },
        onCancel = { toRevoke = null },
        danger = true,
    )
}

/** How many changes the client made, and the latest of them, e.g. "Neuigkeit 12 · update_news · Sa 24.10.2026, 17:40". */
@Composable
private fun ChangesCell(connection: McpConnection) {
    if (connection.changeCount == 0) {
        Text("keine")
        return
    }
    Text(if (connection.changeCount == 1) "1 Änderung" else "${connection.changeCount} Änderungen")
    Ul({ classes(connections.element("changes").className) }) {
        connection.recentChanges.forEach { change ->
            Li {
                Text(listOfNotNull("${change.recordType.label} ${change.recordId.orEmpty()}".trim(), change.tool, formatDateTime(change.changedAt)).joinToString(" · "))
            }
        }
    }
}
